import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactMethodsGrid = ({ onMethodSelect }) => {
  const contactMethods = [
    {
      id: 'phone',
      icon: 'Phone',
      title: 'Phone Consultation',
      description: 'Speak directly with our specialists for immediate assistance and personalized guidance.',
      availability: 'Mon-Fri: 08:00-20:00 GMT',
      action: 'Call Now',
      color: 'accent',
      numbers: [
        { region: 'UK Office', number: '+44 20 7946 0958' },
        { region: 'African Hub', number: '+234 1 460 2345' }
      ]
    },
    {
      id: 'video',
      icon: 'Video',
      title: 'Video Consultation',
      description: 'Schedule a face-to-face video meeting with our transfer specialists and career advisors.',
      availability: 'Book 24-48h advance',
      action: 'Schedule Video Call',
      color: 'secondary',
      features: ['Screen sharing', 'Document review', 'Multi-party calls']
    },
    {
      id: 'email',
      icon: 'Mail',
      title: 'Email Inquiry',
      description: 'Send detailed inquiries and receive comprehensive responses with supporting documentation.',
      availability: 'Response within 24h',
      action: 'Send Email',
      color: 'primary',
      addresses: [
        { type: 'General', email: 'contact@prox11market.com' },
        { type: 'Clubs', email: 'clubs@prox11market.com' },
        { type: 'Players', email: 'players@prox11market.com' }
      ]
    },
    {
      id: 'chat',
      icon: 'MessageCircle',
      title: 'Live Chat',
      description: 'Get instant answers from our AI-powered assistant with seamless handoff to human specialists.',
      availability: 'Available 24/7',
      action: 'Start Chat',
      color: 'success',
      features: ['Instant response', 'File sharing', 'Chat history']
    },
    {
      id: 'office',
      icon: 'MapPin',
      title: 'Office Visit',
      description: 'Visit our offices for in-person consultations and comprehensive service presentations.',
      availability: 'By appointment only',
      action: 'Book Visit',
      color: 'warning',
      locations: [
        { city: 'London', address: '123 Canary Wharf, E14 5AB' },
        { city: 'Lagos', address: '45 Victoria Island, Lagos' }
      ]
    },
    {
      id: 'whatsapp',
      icon: 'MessageSquare',
      title: 'WhatsApp Business',
      description: 'Connect via WhatsApp for quick updates, document sharing, and convenient communication.',
      availability: 'Mon-Sat: 07:00-22:00 GMT',
      action: 'Message on WhatsApp',
      color: 'secondary',
      number: '+44 7700 900123'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      accent: 'bg-accent/10 text-accent border-accent/20',
      secondary: 'bg-secondary/10 text-secondary border-secondary/20',
      primary: 'bg-primary/10 text-primary border-primary/20',
      success: 'bg-success/10 text-success border-success/20',
      warning: 'bg-warning/10 text-warning border-warning/20'
    };
    return colors?.[color] || colors?.primary;
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="Headphones" size={16} className="text-primary" />
            <span className="text-xs md:text-sm font-medium text-primary">Multiple Contact Channels</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-3 md:mb-4">
            Choose Your Preferred Communication Method
          </h2>
          
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
            We offer flexible communication options to suit your preferences and urgency level. All channels connect you with our expert team for professional, FIFA-compliant guidance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {contactMethods?.map((method) => (
            <div
              key={method?.id}
              className="group bg-card border border-border rounded-xl p-4 md:p-6 hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center border ${getColorClasses(method?.color)} transition-transform duration-300 group-hover:scale-110`}>
                  <Icon name={method?.icon} size={24} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg md:text-xl font-bold font-headline text-foreground mb-1">
                    {method?.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    <span>{method?.availability}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm md:text-base text-muted-foreground mb-4 line-clamp-3">
                {method?.description}
              </p>

              {method?.numbers && (
                <div className="space-y-2 mb-4">
                  {method?.numbers?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <span className="text-xs md:text-sm font-medium text-foreground">{item?.region}</span>
                      <span className="text-xs md:text-sm font-mono text-primary">{item?.number}</span>
                    </div>
                  ))}
                </div>
              )}

              {method?.addresses && (
                <div className="space-y-2 mb-4">
                  {method?.addresses?.map((item, index) => (
                    <div key={index} className="p-2 bg-muted/50 rounded-lg">
                      <div className="text-xs font-medium text-foreground mb-1">{item?.type}</div>
                      <div className="text-xs md:text-sm font-mono text-primary break-all">{item?.email}</div>
                    </div>
                  ))}
                </div>
              )}

              {method?.locations && (
                <div className="space-y-2 mb-4">
                  {method?.locations?.map((item, index) => (
                    <div key={index} className="p-2 bg-muted/50 rounded-lg">
                      <div className="text-xs md:text-sm font-medium text-foreground">{item?.city}</div>
                      <div className="text-xs text-muted-foreground">{item?.address}</div>
                    </div>
                  ))}
                </div>
              )}

              {method?.features && (
                <div className="space-y-2 mb-4">
                  {method?.features?.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Icon name="Check" size={14} className="text-success flex-shrink-0" />
                      <span className="text-xs md:text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              {method?.number && (
                <div className="p-3 bg-muted/50 rounded-lg mb-4">
                  <div className="text-xs font-medium text-foreground mb-1">WhatsApp Number</div>
                  <div className="text-sm md:text-base font-mono text-primary">{method?.number}</div>
                </div>
              )}

              <Button
                variant="outline"
                fullWidth
                iconName={method?.icon}
                iconPosition="left"
                onClick={() => onMethodSelect(method?.id)}
                className="group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary"
              >
                {method?.action}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactMethodsGrid;